import { toast } from "sonner";
import { TriangleAlertIcon } from "lucide-react";

import { CampoDeCastigo } from "@/components/generacion/CampoDeCastigo";
import { useFijarCastigos } from "@/hooks/useValorizacion";
import { getApiErrorMessage } from "@/lib/api";
import { cn } from "@/lib/utils";
import { formatearFecha } from "@/lib/fechas";
import { formatearPrecio, rubrosPorFijar, textoOGuion, type MontoEnTresMonedas, type Valorizacion } from "@/lib/valorizacion";

interface TotalesDeLaValorizacionProps {
  informeId: number;
  valorizacion: Valorizacion;
}

/**
 * El valor comercial del predio, su valor de liquidación y lo que falta para
 * que la cifra esté completa.
 *
 * Todo viene calculado y formateado por el servidor: acá no se suma ni se
 * convierte nada. Una cifra que no se pudo calcular se imprime «—», y una
 * liquidación sin castigo dice que está pendiente en vez de valer cero.
 */
export function TotalesDeLaValorizacion({ informeId, valorizacion }: TotalesDeLaValorizacionProps) {
  const castigos = useFijarCastigos(informeId);
  const { liquidacion, avaluo_fiscal: avaluo, tipo_cambio: tipoCambio } = valorizacion;
  const sinValor = rubrosPorFijar(valorizacion).length;
  const liquidacionPendiente = liquidacion.total_formateado.uf === null;

  return (
    <section
      aria-label="Totales de la valorización"
      className="bg-card flex flex-col gap-3.5 rounded-[1.25rem] border-[1.5px] px-5 py-[1.125rem] xl:sticky xl:top-3"
    >
      <div>
        <h3 className="rotulo-seccion">Valor comercial</h3>
        <Cifras monto={valorizacion.total_formateado} grande />
        {sinValor > 0 ? (
          <p className="text-warning-texto bg-warning-soft mt-2 rounded-lg px-2.5 py-1.5 text-xs font-bold">
            Total parcial: {sinValor} {sinValor === 1 ? "rubro sin valor" : "rubros sin valor"}
          </p>
        ) : null}
        {valorizacion.pendientes.length > 0 ? (
          <ul className="text-ink-secundario mt-2 flex flex-col gap-1 text-xs font-semibold">
            {valorizacion.pendientes.map((p, i) => (
              <li key={`${p.codigo}-${p.rubro ?? i}`} className="flex gap-1.5">
                <TriangleAlertIcon aria-hidden className="text-warning-dot mt-0.5 size-3.5 shrink-0" />
                <span>{p.detalle}</span>
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      <div className="border-divisor-fuerte border-t-[1.5px] pt-3">
        <h3 className="rotulo-seccion">Por hectárea</h3>
        <p className="text-ink font-mono text-[0.9375rem] font-bold">{textoOGuion(valorizacion.valor_por_ha_formateado)}</p>
      </div>

      {avaluo.total_clp !== null ? (
        <div className="border-divisor-fuerte border-t-[1.5px] pt-3">
          <h3 className="rotulo-seccion">Avalúo fiscal</h3>
          <p className="text-ink font-mono text-[0.9375rem] font-bold">{textoOGuion(avaluo.total_formateado)}</p>
          {avaluo.relacion_valor_comercial !== null ? (
            <p className="text-ink-muted text-xs font-semibold">
              El valor comercial es {avaluo.relacion_valor_comercial.toLocaleString("es-CL")} veces el avalúo fiscal.
            </p>
          ) : null}
        </div>
      ) : null}

      <div className="border-divisor-fuerte flex flex-col gap-2 border-t-[1.5px] pt-3">
        <h3 className="rotulo-seccion">Valor de liquidación</h3>
        {liquidacionPendiente ? (
          <p className="text-ink-medio text-xs font-bold">Liquidación pendiente: falta el castigo</p>
        ) : (
          <Cifras monto={liquidacion.total_formateado} />
        )}
        <span className="text-ink text-xs font-bold">Castigo global</span>
        <CampoDeCastigo
          key={liquidacion.castigo_global_pct ?? "sin-castigo"}
          etiqueta="Castigo global (%)"
          etiquetaDelBoton="Guardar castigo global"
          valor={liquidacion.castigo_global_pct}
          placeholder="Sin castigo"
          guardando={castigos.isPending}
          onGuardar={(valor) =>
            castigos.mutate(
              { global: valor },
              { onError: (falla) => toast.error(getApiErrorMessage(falla, "No pudimos guardar el castigo.")) },
            )
          }
        />
        <p className="text-ink-medio text-xs font-semibold">Cada rubro puede tener el suyo; vacío usa el global.</p>
      </div>

      <p className="text-ink-medio border-divisor-fuerte border-t-[1.5px] pt-3 text-[0.6875rem] leading-relaxed font-semibold">{pieDelTipoDeCambio(tipoCambio)}</p>
    </section>
  );
}

function Cifras({ monto, grande }: { monto: MontoEnTresMonedas<string>; grande?: boolean }) {
  return (
    <div className="mt-1 flex flex-col font-mono">
      <span className={cn("text-ink font-bold", grande ? "text-[1.625rem] leading-tight" : "text-[1.1875rem]")}>
        {textoOGuion(monto.uf)}
      </span>
      <span className="text-ink-secundario text-[0.8125rem] font-semibold">{textoOGuion(monto.clp)}</span>
      <span className="text-ink-secundario text-[0.8125rem] font-semibold">{textoOGuion(monto.usd)}</span>
    </div>
  );
}

function pieDelTipoDeCambio(tipoCambio: Valorizacion["tipo_cambio"]): string {
  if (tipoCambio.fecha_valorizacion === null || (tipoCambio.uf === null && tipoCambio.usd === null)) {
    return "Sin tipo de cambio: los totales están solo en UF.";
  }

  return [
    `Tipo de cambio del ${formatearFecha(tipoCambio.fecha_valorizacion)}`,
    tipoCambio.uf === null ? "UF sin dato" : `UF ${formatearPrecio(tipoCambio.uf, "CLP")}`,
    tipoCambio.usd === null ? "US$ sin dato" : `US$ ${formatearPrecio(tipoCambio.usd, "CLP")}`,
  ].join(" · ");
}
